// Implements: F-IDN-014, F-UIX-009, F-UIX-013
/**
 * The two-step verification card when nothing is being enrolled (v3 `acctTotp`, states `off` and
 * `on`). Off: what it is for and "Activar". On: since when, and "Desactivar", which asks first in a
 * dialog for a current six-digit code (never `confirm()`, F-UIX-013). "Último uso" is the time of
 * the last accepted code, "Hoy, 10:24" or a date (owner Default P1-3: never a place), and no row at
 * all before any code was accepted. Pure presentation: the code
 * is checked for six digits here, sent through `onDisable`, and a refusal comes back as
 * `disableError`.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { RateLimitWait } from "../../../shared/ui/molecules/RateLimitWait";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import { dateText, lastUseText } from "../model/dates";
import { TotpHeader } from "./TotpHeader";
import shared from "./account.module.css";

export interface TotpPanelProps {
  readonly enabled: boolean;
  readonly since: string | null;
  /** v3 "Último uso" (Default P1-3: the time only, no place); `null` before any accepted code. */
  readonly lastUsed: string | null;
  /** The reader's "now", which decides "Hoy" (injectable, checklist 16). */
  readonly now?: Date | undefined;
  readonly onActivate: () => void;
  readonly disableOpen: boolean;
  readonly disabling: boolean;
  readonly disableError: string | null;
  /** Seconds left of a `429` on the disabling, or `null` when nothing is to be waited. */
  readonly waitSeconds: number | null;
  readonly onAskDisable: () => void;
  readonly onCancelDisable: () => void;
  readonly onDisable: (code: string) => void;
}

const SIX_DIGITS = /^\d{6}$/;

export function TotpPanel(props: TotpPanelProps): ReactElement {
  const {
    enabled,
    since,
    lastUsed,
    now,
    onActivate,
    disableOpen,
    disabling,
    disableError,
    waitSeconds,
    onAskDisable,
    onCancelDisable,
    onDisable,
  } = props;
  const { t } = useTranslation();
  const titleId = useId();
  const codeId = useId();
  const [code, setCode] = useState("");
  const [digitsError, setDigitsError] = useState(false);
  const error = digitsError ? t("account.totp.disableDigits") : disableError;

  const submitDisable = (): void => {
    if (!SIX_DIGITS.test(code)) {
      setDigitsError(true);
      return;
    }
    setDigitsError(false);
    onDisable(code);
  };

  const sinceDate = dateText(since, "short");
  const lastUse = lastUseText(lastUsed, now ?? new Date(), (time) => t("account.totp.lastUsedToday", { time }));
  const badge = enabled ? (
    <Badge tone="success">{t("account.totp.badgeOn", { date: sinceDate })}</Badge>
  ) : (
    <Badge tone="neutral">{t("account.totp.badgeOff")}</Badge>
  );

  return (
    <section className={shared.card} aria-labelledby={titleId}>
      <TotpHeader titleId={titleId} badge={badge} />
      <div className={shared.body}>
        {enabled ? (
          <>
            <p className={shared.text}>{t("account.totp.onBody")}</p>
            <dl className={shared.details}>
              <dt>{t("account.totp.activatedAt")}</dt>
              <dd>{dateText(since, "moment", "—")}</dd>
              <dt>{t("account.totp.app")}</dt>
              <dd>{t("account.totp.appValue")}</dd>
              {lastUse === null ? null : (
                <>
                  <dt>{t("account.totp.lastUsed")}</dt>
                  <dd>{lastUse}</dd>
                </>
              )}
            </dl>
          </>
        ) : (
          <p className={shared.text}>{t("account.totp.intro")}</p>
        )}
      </div>
      <div className={shared.foot}>
        {enabled ? (
          <Button
            variant="danger"
            hasPopup="dialog"
            onClick={() => {
              setCode("");
              setDigitsError(false);
              onAskDisable();
            }}
          >
            {t("account.totp.disable")}
          </Button>
        ) : (
          <Button variant="primary" onClick={onActivate}>
            <Icon name="lock" size="sm" />
            {t("account.totp.activate")}
          </Button>
        )}
      </div>
      <Dialog
        open={disableOpen}
        title={t("account.totp.disableTitle")}
        onClose={onCancelDisable}
        footer={
          <>
            <Button variant="secondary" onClick={onCancelDisable}>
              {t("account.totp.cancel")}
            </Button>
            <Button variant="danger" loading={disabling} disabled={disabling || waitSeconds !== null} onClick={submitDisable}>
              {t("account.totp.disable")}
            </Button>
          </>
        }
      >
        <p className={shared.text}>{t("account.totp.disableBody")}</p>
        {waitSeconds === null ? null : <RateLimitWait seconds={waitSeconds} />}
        <div className={shared.field}>
          <label className={shared.label} htmlFor={codeId}>
            {t("account.totp.disableCode")}
          </label>
          <Input
            id={codeId}
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            invalid={error !== null}
            describedBy={error === null ? undefined : `${codeId}-error`}
            onChange={(value) => {
              setCode(value.replace(/\D/g, "").slice(0, 6));
              setDigitsError(false);
            }}
            onEnter={submitDisable}
          />
          {error === null ? null : (
            <p className={shared.error} id={`${codeId}-error`} role="alert">
              {error}
            </p>
          )}
        </div>
      </Dialog>
    </section>
  );
}
